import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, ChevronRight, User, Shield, Star, Eye, Zap, Users, GitBranch } from 'lucide-react';
import type { Contact, ContactUseCaseScore } from '../../../types';
import {
  buildOrgTree,
  getHighlightFromPriority,
  getEffectivePriority,
  HIGHLIGHT_COLORS,
  HIGHLIGHT_LABELS,
  BUYING_ROLE_LABELS,
  SENIORITY_LABELS,
  PRIORITY_LABELS,
  type OrgNode,
  type ContactHighlight,
} from '../../../lib/orgChartUtils';

interface Props {
  contacts: Contact[];
  ucScoreMap: Map<string, ContactUseCaseScore>;
  onSelectContact: (contact: Contact) => void;
  selectedContactId?: string;
  onAssignManager: (contact: Contact) => void;
  focusContactId?: string | null;
  onFocusHandled?: () => void;
}

function HighlightLegend() {
  return (
    <div className="flex flex-wrap gap-3 px-4 py-2.5 bg-app-canvas rounded-lg border border-app-line">
      {(Object.entries(HIGHLIGHT_COLORS) as [ContactHighlight, typeof HIGHLIGHT_COLORS[ContactHighlight]][]).map(([key, colors]) => (
        <div key={key} className="flex items-center gap-1.5">
          <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
          <span className="text-[11px] text-app-muted">{HIGHLIGHT_LABELS[key]}</span>
        </div>
      ))}
    </div>
  );
}

function BuyingRoleIcon({ role }: { role: string }) {
  switch (role) {
    case 'economic_buyer': return <Shield className="w-3 h-3 text-app-critical" />;
    case 'technical_buyer': return <Zap className="w-3 h-3 text-app-focus" />;
    case 'champion': return <Star className="w-3 h-3 text-app-warning" />;
    case 'influencer': return <Users className="w-3 h-3 text-app-warning" />;
    case 'validator': return <Eye className="w-3 h-3 text-app-focus" />;
    default: return null;
  }
}

function OrgNodeRow({ node, depth, onSelect, selectedId, scoreMap, onAssignManager, forceExpandIds }: {
  node: OrgNode;
  depth: number;
  onSelect: (c: Contact) => void;
  selectedId?: string;
  scoreMap: Map<string, ContactUseCaseScore>;
  onAssignManager: (c: Contact) => void;
  forceExpandIds?: Set<string>;
}) {
  const shouldForceExpand = forceExpandIds?.has(node.contact.id) ?? false;
  const [expanded, setExpanded] = useState(depth < 2 || shouldForceExpand);
  const contact = node.contact;

  useEffect(() => {
    if (shouldForceExpand && !expanded) setExpanded(true);
  }, [shouldForceExpand]);
  const ucScore = scoreMap.get(contact.id);

  const effectivePriority = ucScore ? getEffectivePriority(ucScore) : null;
  const highlight = effectivePriority
    ? getHighlightFromPriority(effectivePriority)
    : 'low';
  const colors = HIGHLIGHT_COLORS[highlight];
  const isSelected = selectedId === contact.id;
  const hasChildren = node.children.length > 0;
  const isOverridden = ucScore?.priority_override != null;

  const priorityLabel = effectivePriority
    ? PRIORITY_LABELS[effectivePriority] || ''
    : '';

  return (
    <div className={depth > 0 ? 'ml-6 border-l-2 border-app-line pl-4' : ''}>
      <div className="flex items-start gap-2 mb-2" data-contact-id={contact.id}>
        {hasChildren && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="mt-2 p-0.5 text-app-muted hover:text-app-muted rounded"
          >
            {expanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        )}
        {!hasChildren && <div className="w-[18px]" />}

        <button
          onClick={() => onSelect(contact)}
          className={`flex-1 text-left p-3 rounded-lg border-2 transition hover:shadow-none ${
            isSelected
              ? 'border-forest-500 bg-forest-50 ring-2 ring-forest-200'
              : `${colors.bg} ${colors.border}`
          }`}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                <User className={`w-4 h-4 ${colors.text}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-app-ink truncate">
                    {contact.first_name} {contact.last_name}
                  </p>
                  <BuyingRoleIcon role={contact.buying_committee_role} />
                </div>
                <p className="text-xs text-app-muted truncate">{contact.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <div className="flex flex-col items-end gap-1">
                {priorityLabel && highlight !== 'low' && (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                    {priorityLabel}
                    {isOverridden && <span className="ml-1 text-[8px] text-app-warning">(OVR)</span>}
                  </span>
                )}
                {ucScore?.persona_match && (
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-forest-100 text-forest-700 truncate max-w-[120px]">
                    {ucScore.persona_match}
                  </span>
                )}
                {contact.buying_committee_role && (
                  <span className="text-[10px] text-app-muted">
                    {BUYING_ROLE_LABELS[contact.buying_committee_role]}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 mt-2">
            {contact.department && (
              <span className="text-[10px] px-1.5 py-0.5 bg-app-canvas rounded text-app-muted">
                {contact.department}
              </span>
            )}
            {contact.seniority && (
              <span className="text-[10px] text-app-muted">
                {SENIORITY_LABELS[contact.seniority]?.split('(')[0]?.trim()}
              </span>
            )}
            {ucScore && (
              <span className="text-[10px] text-app-muted ml-auto">
                {ucScore.use_case_relevance_score}% relevance
              </span>
            )}
          </div>
        </button>

        <button
          onClick={e => {
            e.stopPropagation();
            onAssignManager(contact);
          }}
          className="mt-2 p-1.5 text-app-line-strong hover:text-forest-600 hover:bg-forest-50 rounded-lg transition-colors flex-shrink-0"
          title="Assign manager"
        >
          <GitBranch className="w-3.5 h-3.5" />
        </button>
      </div>

      {expanded && hasChildren && (
        <div className="space-y-0">
          {node.children.map(child => (
            <OrgNodeRow
              key={child.contact.id}
              node={child}
              depth={depth + 1}
              onSelect={onSelect}
              selectedId={selectedId}
              scoreMap={scoreMap}
              onAssignManager={onAssignManager}
              forceExpandIds={forceExpandIds}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function getAncestorIds(contacts: Contact[], targetId: string): Set<string> {
  const parentMap = new Map<string, string>();
  for (const c of contacts) {
    if (c.reports_to_id) parentMap.set(c.id, c.reports_to_id);
  }
  const ids = new Set<string>();
  let current = parentMap.get(targetId);
  while (current) {
    ids.add(current);
    current = parentMap.get(current);
  }
  return ids;
}

export default function OrgChartList({ contacts, ucScoreMap, onSelectContact, selectedContactId, onAssignManager, focusContactId, onFocusHandled }: Props) {
  const tree = buildOrgTree(contacts);
  const listRef = useRef<HTMLDivElement>(null);
  const [forceExpandIds, setForceExpandIds] = useState<Set<string>>(new Set());

  const scrollToContact = useCallback((contactId: string) => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-contact-id="${contactId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, []);

  useEffect(() => {
    if (!focusContactId) return;
    const ancestors = getAncestorIds(contacts, focusContactId);
    setForceExpandIds(ancestors);
    const timer = setTimeout(() => {
      scrollToContact(focusContactId);
      onFocusHandled?.();
    }, 150);
    return () => clearTimeout(timer);
  }, [focusContactId, contacts, scrollToContact, onFocusHandled]);

  if (contacts.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
        <p className="text-sm text-app-muted">No contacts to display.</p>
      </div>
    );
  }

  const deptGroups = new Map<string, Contact[]>();
  contacts.forEach(c => {
    const dept = c.department || 'General';
    if (!deptGroups.has(dept)) deptGroups.set(dept, []);
    deptGroups.get(dept)!.push(c);
  });

  const avgConfidence = Math.round(
    contacts.reduce((sum, c) => sum + c.org_placement_confidence, 0) / contacts.length
  );

  const scoredContacts = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    return s && s.persona_match_score > 0;
  });
  const avgPersonaMatch = scoredContacts.length > 0
    ? Math.round(
        scoredContacts.reduce((sum, c) => sum + (ucScoreMap.get(c.id)?.persona_match_score || 0), 0) /
        scoredContacts.length
      )
    : 0;

  const targetCount = contacts.filter(c => {
    const s = ucScoreMap.get(c.id);
    if (!s) return false;
    const ep = getEffectivePriority(s);
    return ep === 'target_persona' || ep === 'decision_maker';
  }).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Org Accuracy</p>
          <p className="text-lg font-bold text-app-ink">{avgConfidence}%</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Persona Match</p>
          <p className="text-lg font-bold text-app-ink">{avgPersonaMatch}%</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Key Targets</p>
          <p className="text-lg font-bold text-app-ink">{targetCount}</p>
        </div>
        <div className="p-3 bg-app-surface rounded-lg border border-app-line text-center">
          <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider">Departments</p>
          <p className="text-lg font-bold text-app-ink">{deptGroups.size}</p>
        </div>
      </div>

      <HighlightLegend />

      <div ref={listRef} className="space-y-1">
        {tree.map(node => (
          <OrgNodeRow
            key={node.contact.id}
            node={node}
            depth={0}
            onSelect={onSelectContact}
            selectedId={selectedContactId}
            scoreMap={ucScoreMap}
            onAssignManager={onAssignManager}
            forceExpandIds={forceExpandIds}
          />
        ))}
      </div>
    </div>
  );
}
